import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import CakeIcon from '@mui/icons-material/Cake';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import CelebrationIcon from '@mui/icons-material/Celebration';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import { formatDateStandard } from '../utils/dateFormat';
import {
  getDaysUntilBirthday,
  getDaysUntilWorkAnniversary,
  getYearsOfService,
} from '../utils/employeeMilestones';

export interface PersonalMilestoneTilesProps {
  dateOfBirth?: string | null;
  dateOfEngagement?: string | null;
  companyName?: string | null;
}

function daysLabel(days: number): string {
  if (days === 0) return 'Today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

const celebrateBirthdaySx = {
  height: '100%',
  border: 'none',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 55%, #6366f1 100%)',
  color: '#fff',
  boxShadow: '0 10px 28px rgba(236, 72, 153, 0.35)',
} as const;

const celebrateAnniversarySx = {
  height: '100%',
  border: 'none',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 45%, #c2410c 100%)',
  color: '#fff',
  boxShadow: '0 10px 28px rgba(245, 158, 11, 0.38)',
} as const;

const celebrateSubtleBodyProps = {
  color: 'inherit',
  sx: { opacity: 0.95, mt: 0.5 },
} as const;

export default function PersonalMilestoneTiles({
  dateOfBirth,
  dateOfEngagement,
  companyName,
}: PersonalMilestoneTilesProps) {
  const service = dateOfEngagement ? getYearsOfService(dateOfEngagement) : null;
  const daysBirthday = getDaysUntilBirthday(dateOfBirth);
  const daysWorkAnniv = getDaysUntilWorkAnniversary(dateOfEngagement);

  const hasService = service !== null && dateOfEngagement;
  const hasBirthday = daysBirthday !== null;
  const hasWorkAnniv = daysWorkAnniv !== null;
  const birthdayToday = daysBirthday === 0;
  const workAnniversaryToday = daysWorkAnniv === 0;

  if (!hasService && !hasBirthday && !hasWorkAnniv) {
    return null;
  }

  return (
    <Grid container spacing={2}>
      {hasService && (
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined" sx={{ height: '100%', borderColor: 'divider' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <EmojiEventsOutlinedIcon sx={{ color: 'primary.main', mt: 0.25 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Years of service
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="primary" sx={{ lineHeight: 1.2 }}>
                    {service!.years}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {companyName ? `with ${companyName}` : 'with us'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Since {formatDateStandard(dateOfEngagement)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
      {hasBirthday && (
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card
            variant={birthdayToday ? 'elevation' : 'outlined'}
            elevation={birthdayToday ? 4 : 0}
            sx={birthdayToday ? celebrateBirthdaySx : { height: '100%', borderColor: 'divider' }}
          >
            <CardContent sx={{ py: 2.25, position: 'relative' }}>
              {birthdayToday && (
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    top: -24,
                    right: -16,
                    opacity: 0.2,
                    transform: 'rotate(12deg)',
                  }}
                >
                  <CelebrationIcon sx={{ fontSize: 112 }} />
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, position: 'relative' }}>
                {birthdayToday ? (
                  <CakeIcon sx={{ color: 'inherit', mt: 0.25, fontSize: 36, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' }} />
                ) : (
                  <CakeOutlinedIcon sx={{ color: 'primary.main', mt: 0.25 }} />
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mb: 0.5, opacity: birthdayToday ? 0.92 : undefined }}
                    color={birthdayToday ? 'inherit' : 'text.secondary'}
                  >
                    Birthday
                  </Typography>
                  {birthdayToday ? (
                    <>
                      <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.25, textShadow: '0 1px 2px rgba(0,0,0,0.12)' }}>
                        Happy birthday!
                      </Typography>
                      <Typography variant="body2" {...celebrateSubtleBodyProps}>
                        Wishing you a wonderful day from all of us.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="h4" fontWeight={700} color="primary" sx={{ lineHeight: 1.2 }}>
                        {daysLabel(daysBirthday!)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        until your birthday
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
      {hasWorkAnniv && (
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card
            variant={workAnniversaryToday ? 'elevation' : 'outlined'}
            elevation={workAnniversaryToday ? 4 : 0}
            sx={workAnniversaryToday ? celebrateAnniversarySx : { height: '100%', borderColor: 'divider' }}
          >
            <CardContent sx={{ py: 2.25, position: 'relative' }}>
              {workAnniversaryToday && (
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    top: -20,
                    right: -12,
                    opacity: 0.22,
                    transform: 'rotate(-8deg)',
                  }}
                >
                  <CelebrationIcon sx={{ fontSize: 104 }} />
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, position: 'relative' }}>
                {workAnniversaryToday ? (
                  <CelebrationIcon sx={{ color: 'inherit', mt: 0.15, fontSize: 36, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' }} />
                ) : (
                  <EventRoundedIcon sx={{ color: 'primary.main', mt: 0.25 }} />
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mb: 0.5, opacity: workAnniversaryToday ? 0.92 : undefined }}
                    color={workAnniversaryToday ? 'inherit' : 'text.secondary'}
                  >
                    Work anniversary
                  </Typography>
                  {workAnniversaryToday ? (
                    <>
                      <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.25, textShadow: '0 1px 2px rgba(0,0,0,0.12)' }}>
                        {service != null ? `${service.years} years — thank you!` : 'Happy work anniversary!'}
                      </Typography>
                      <Typography variant="body2" {...celebrateSubtleBodyProps}>
                        {companyName
                          ? `Celebrating your dedication to ${companyName}.`
                          : 'Celebrating your dedication and everything you bring to the team.'}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="h4" fontWeight={700} color="primary" sx={{ lineHeight: 1.2 }}>
                        {daysLabel(daysWorkAnniv!)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        until your next anniversary
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
}
